import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";

const RATES = { chf: 1.0, eur: 1.05 };
const fmt = (currency, value) =>
  new Intl.NumberFormat(currency === "eur" ? "de-DE" : "de-CH", { style: "currency", currency: (currency || "chf").toUpperCase() }).format(value || 0);
const fmtChf = (o, chf) => fmt(o.currency, (chf || 0) * (RATES[o.currency] || 1));

const statusColor = (s) => s === "paid" ? "text-[#166534]" : s === "pending" || s === "initiated" ? "text-[#C5A880]" : "text-[#991B1B]";

export default function Account() {
  const { user, logout, loading } = useAuth();
  const { t, tl } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) api.get("/orders").then((res) => setOrders(res.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-12 lg:py-16 fade-up min-h-[60vh] max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-serif-display text-4xl text-[#1A1F2C]">{t("my_account")}</h1>
          <p className="text-sm text-[#6B7280] mt-1">{user.name} · {user.email}</p>
        </div>
        <button data-testid="logout-button" onClick={() => { logout(); navigate("/"); }}
          className="text-xs tracking-luxe uppercase border-b border-[#1A1F2C] pb-1 hover:text-[#C5A880] hover:border-[#C5A880] transition-colors">
          {t("logout")}
        </button>
      </div>

      <h2 className="text-xs tracking-luxe uppercase text-[#C5A880] mb-5">{t("my_orders")}</h2>
      {orders.length === 0 ? (
        <p className="text-sm text-[#6B7280]">{t("no_orders")}</p>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="bg-white border border-[#EAE6E1] p-5">
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs tracking-luxe uppercase text-[#6B7280]">{t("order")} · {new Date(o.created_at).toLocaleDateString()}</span>
                <span className={`text-xs tracking-luxe uppercase ${statusColor(o.payment_status)}`}>{o.payment_status}</span>
              </div>
              <div className="space-y-1 mb-3">
                {o.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-[#1A1F2C]">
                    <span>{tl(i.name)} × {i.quantity}</span>
                    <span>{fmtChf(o, i.price * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#EAE6E1] pt-2 flex justify-between font-serif-display text-lg">
                <span>{t("total")}</span><span>{fmt(o.currency, o.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
